import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  AbyssViewerNoContext,
  PointCloudMaterialProps,
  PointPickResult,
  SphericalImageProps,
  SphericalImageViewerClickEvent,
  MarkerProps,
  PolygonProps,
  FloorLevelProps,
  AbyssViewerComponentProps,
  MeasurementLinesProps,
  UnitSystemEnum,
  ImageProps,
  ImageSetProps,
  SphericalImageSetProps,
  useAbyssViewerContext,
  SvgMarkerSetProps,
  ViewerFileType,
  useNeighbouringImages,
  ImageType,
  NeighbouringImageProps,
  NeighbouringImageSet,
  ImagesByDirection,
  PointCloudInfo,
  transformFromAlignedBox,
  VisibilityBoxMode,
  ViewCubeProps,
  Number3,
  HtmlMarkerSetProps,
} from '@abyss/3d-viewer';
import * as Sentry from '@sentry/browser';
import { MenuBar } from './MenuBar';

import * as state from '@/components/Analysis/state';
import * as globalState from '@/state';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { CUBE_PADDING_X, CUBE_PADDING_Y, SPHERICAL_IMAGE_VISIBILITY_RANGE } from '@/constants';
import { useDrawerOpen } from '@/hooks/useDrawerOpen';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { calculateCubePosition, getLastTwoArrayItems } from '@/utils';
import {
  updateCameraTargetUrlParameters,
  useQueryParametersFromState,
  useSetStateFromQueryParameters,
} from '@/components/Analysis/deeplink';
import { PageLoader } from '@/components/shared/PageLoader';
import { LowFpsPopupTrigger } from '../LowFpsPopupTrigger';
import { useViewerPerformanceAlert } from '@/hooks/useViewerPerformanceAlert';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { ShadowBar } from './MenuBar/styles';
import { usePaintRegionLazyQuery } from '@/__generated__/graphql';
import { Alert, AlertTitle, Box } from '@mui/material';
import { useAssemblyCadModels } from '@/components/Analysis/modules/assembly/useAssemblyCadModels';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCuboidVisibilityBox } from '@/modules/cuboids/hooks/useCuboidVisibilityBox';
import { useCuboidState } from '@/components/Analysis/modules/cuboids/hooks';

// const cubePadding = 24;
const SHOW_MAX_NEIGHBOURING_IMAGES = 20;
const MAX_NEIGHBOURING_IMAGE_ANGLE = 45;

type Props = {
  children: React.ReactNode;
  assemblyLoading: boolean;
  octreePointCloudsJsonPath: {
    path: string;
    name?: string | string[];
    nodesToShow?: Set<string>;
    contents: {
      'part-id'?: boolean;
      'corrosion-id'?: boolean;
      'rusting-id'?: boolean;
      color?: boolean;
    }[];
    onOctreeHierarchyLoaded: (info: PointCloudInfo) => void;
  }[];
  onModelClick: (point: Number3) => void;
  onPointCloudClick?: (pickResult: PointPickResult) => void;
  onPointCloudSelection?: (pointPickResults: PointPickResult[]) => void;
  onSphericalViewClick?: (event: SphericalImageViewerClickEvent) => void;
  pointCloudMaterials?: Map<string, PointCloudMaterialProps>;
  sphericalImages?: SphericalImageProps[];
  rectilinearImages?: ImageProps[];
  floorLevels: FloorLevelProps[];
  selectionColors?: { shiftKeyColor: number; altKeyColor: number };
  markers?: MarkerProps[];
  svgMarkerSets?: SvgMarkerSetProps[];
  polygons?: PolygonProps[];
  measurementLines?: MeasurementLinesProps;
  material?: string;
  displayAnimationEditor: boolean;
  htmlMarkerSets?: HtmlMarkerSetProps[];
} & JSX.IntrinsicAttributes;

export const AbyssViewer = ({
  children,
  assemblyLoading,
  octreePointCloudsJsonPath,
  onPointCloudClick,
  onPointCloudSelection,
  onModelClick,
  onSphericalViewClick,
  pointCloudMaterials,
  sphericalImages,
  rectilinearImages,
  selectionColors,
  markers,
  svgMarkerSets,
  polygons,
  material = 'annotation', // Default material is annotation
  floorLevels,
  measurementLines,
  displayAnimationEditor,
  htmlMarkerSets,
}: Props) => {
  const { callbacks } = useAbyssViewerContext();
  const cancelPOIAdd = useSetAtom(poiState.cancelPOIAdd);
  const cameraTarget = useAtomValue(state.cameraTarget);
  const inspectionMetadata = useAtomValue(state.inspectionMetadata);
  const [selectedSpherical, setSelectedSpherical] = useAtom(state.selectedSpherical);
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const [applyFilters, setApplyFilters] = useAtom(state.applyFilters);
  const selectedPaintRegionIds = useAtomValue(state.selectedFilteredPaintRegionIds);
  const areSphericalsVisible = useAtomValue(state.areSphericalsVisible);
  const areNeighbouringImagesVisible = useAtomValue(state.areNeighbouringImagesVisible);
  const edlStrength = useAtomValue(globalState.edlStrength);
  const isDrawerOpen = useDrawerOpen('right');
  const setGlobalCallbacks = useSetAtom(state.globalCallbacks);
  const structureRelationship = useAtomValue(state.structureRelationship);

  const cuboidState = useCuboidState();

  const setPaintRegionVisibilityBox = useSetAtom(state.paintRegionVisibilityBox);

  const activeVisibilityBox = useAtomValue(state.activeVisibilityBox);

  const setVisitedViewpointIDs = useSetAtom(state.visitedViewpointIDs);

  const [currentSpherical, setCurrentSpherical] = useAtom(state.viewerCurrentSpherical);
  const [nextCurrentSpherical, setNextCurrentSpherical] = useAtom(state.viewerNextCurrentSpherical);
  const unitSystem = useAtomValue(state.unitSystem);
  const isBetaUser = useFeatureFlag('beta-user');
  const brightness = useAtomValue(globalState.brightness);
  const contrast = useAtomValue(globalState.contrast);

  const [isInitialPointCloudLoaded, setIsInitialPointCloudLoaded] = useState(false);

  const screenSize = useWindowSize();

  useSetStateFromQueryParameters();
  useQueryParametersFromState();

  const [loadErrorAlert, setLoadErrorAlert] = useState<ReactNode>();

  const onFileLoadError = useCallback(
    (fileType: ViewerFileType, url: string, error: Error, info: React.ErrorInfo) => {
      Sentry.captureMessage(error.message);
      console.warn('File load error', { fileType, url, error, info });
      if (fileType === ViewerFileType.VIEWER_IMAGE) {
        // exit viewer mode
        setTimeout(() => setSelectedSpherical(undefined), 800);
        setLoadErrorAlert(
          <Box sx={{ margin: 'auto', width: '50%' }}>
            <Alert
              sx={{
                position: 'absolute',
                top: '25%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 2,
              }}
              severity="error"
              onClose={() => setLoadErrorAlert(undefined)}
            >
              <AlertTitle>File Load Error</AlertTitle>
              {/* {error.message} */}
              Unable to load image {url.split('/').slice(-2).join('/')}
            </Alert>
          </Box>
        );
      }
    },
    [setSelectedSpherical]
  );

  const { setSmallVisibilityBox, smallVisibilityBox } = useCuboidVisibilityBox(cuboidState);

  const cubeSize = useMemo(() => {
    const { width } = screenSize;
    if (width && width <= 1728) {
      return 40;
    }
    if (width && width <= 1440) {
      return 50;
    }
    return 60;
  }, [screenSize]);

  const cubePadding = useMemo(() => {
    const { width } = screenSize;
    if (width && width <= 1728) {
      return 52;
    }
    return 24;
  }, [screenSize]);
  const [fetchPaintRegionVolume] = usePaintRegionLazyQuery();

  useEffect(() => {
    setApplyFilters(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (applyFilters) {
      if (selectedPaintRegionIds.length === 1) {
        fetchPaintRegionVolume({
          variables: {
            paintRegionId: selectedPaintRegionIds[0],
          },
        }).then(({ data }) => {
          if (data?.paintRegion?.volume) {
            const xValues = data?.paintRegion?.volume.inclusion.xy_polygon.map((item) => item[0]);
            const yValues = data?.paintRegion?.volume.inclusion.xy_polygon.map((item) => item[1]);

            const boxMinX = Math.min(...xValues);
            const boxMaxX = Math.max(...xValues);
            const boxMinY = Math.min(...yValues);
            const boxMaxY = Math.max(...yValues);
            const boxMinZ = data?.paintRegion?.volume?.inclusion.z_range[0];
            const boxMaxZ = data?.paintRegion?.volume?.inclusion.z_range[1];

            setPaintRegionVisibilityBox({
              ...transformFromAlignedBox({
                min: [boxMinX, boxMinY, boxMinZ],
                max: [boxMaxX, boxMaxY, boxMaxZ],
              }),
              mode: VisibilityBoxMode.Enabled,
              step: [0.25, 0.25, 0.1],
            });
          }
        });
      } else {
        setPaintRegionVisibilityBox(undefined);
      }
    }
  }, [applyFilters, fetchPaintRegionVolume, selectedPaintRegionIds, setPaintRegionVisibilityBox]);

  useEffect(() => {
    // Entering image viewpoint
    if (currentSpherical) {
      setVisitedViewpointIDs((currentVisitedViewpointIDs) => {
        const lastTwoVisitedViewpoints = getLastTwoArrayItems(currentVisitedViewpointIDs);
        // Making sure only the last two visited viewpoints are stored
        if (!lastTwoVisitedViewpoints) {
          return [...currentVisitedViewpointIDs, currentSpherical?.id ?? ''];
        }
        if (lastTwoVisitedViewpoints.length === 1) {
          return [lastTwoVisitedViewpoints[0], currentSpherical?.id ?? ''];
        }
        if (lastTwoVisitedViewpoints.length > 1) {
          return [lastTwoVisitedViewpoints[1], currentSpherical?.id ?? ''];
        }
        return currentVisitedViewpointIDs;
      });
    } else {
      // Exiting image viewpoint
      cancelPOIAdd();
      setVisitedViewpointIDs((previous) => [...previous, previous?.[previous.length - 1]]);
    }
  }, [currentSpherical, setVisitedViewpointIDs, cancelPOIAdd]);

  const { rightDrawerWidth } = useDrawerWidths();

  const positionMemo: [number, number, number] = useMemo(() => {
    const { width, height } = screenSize;

    if (!width || !height) {
      return [CUBE_PADDING_X - rightDrawerWidth, CUBE_PADDING_Y, 0];
    }

    const [x, y, z] = calculateCubePosition({ width, height, cubeSize, padding: cubePadding });

    if (isDrawerOpen) {
      return [x - rightDrawerWidth, y, z];
    }
    return [x, y, z];
  }, [cubePadding, cubeSize, isDrawerOpen, rightDrawerWidth, screenSize]);

  const viewCube: ViewCubeProps = useMemo(
    () => ({
      position: positionMemo,
      cubeSize,
      fontSize: 60,
      colors: {
        foreground: '#000',
        background: '#CCC',
        hoverForeground: '#000',
        hoverBackground: '#44AAFF',
      },
      compass: {
        ringColor: '#CCC',
        letterStyle: { fontSize: 18, color: '#CCC', strokeColor: '#111', strokeWidth: 1 },
      },
    }),
    [positionMemo, cubeSize]
  );

  const pointClouds = useMemo(
    () =>
      octreePointCloudsJsonPath?.map(({ path, name, nodesToShow, onOctreeHierarchyLoaded }) => ({
        name: `Line ${name}`,
        id: `Octree Point Cloud - ${path}`,
        url: path,
        material,
        isVisible: true,
        nodesToShow,
        onOctreeHierarchyLoaded,
      })),
    [material, octreePointCloudsJsonPath]
  );

  const models = useAssemblyCadModels({ onModelClick });

  useEffect(() => {
    if (callbacks) {
      setGlobalCallbacks(callbacks);
    }
  }, [callbacks, setGlobalCallbacks]);

  const centerOnLoad = useCallback(() => {
    setIsInitialPointCloudLoaded(true);
    if (!cameraTarget && callbacks?.resetCameraPositionToDefault) {
      callbacks.resetCameraPositionToDefault();
    }
  }, [setIsInitialPointCloudLoaded, cameraTarget, callbacks]);

  const imageSets = useMemo<ImageSetProps[]>(() => {
    return [
      {
        id: 'imageSet',
        name: 'imageSet',
        images: rectilinearImages ?? [],
        isVisible: rectilinearImages && areSphericalsVisible,
      },
    ];
  }, [rectilinearImages, areSphericalsVisible]);

  const sphericalImageSets = useMemo<SphericalImageSetProps[]>(() => {
    return [
      {
        id: 'sphericalImageSet',
        name: 'sphericalImageSet',
        sphericalImages: sphericalImages ?? [],
        isVisible: sphericalImages && areSphericalsVisible,
      },
    ];
  }, [sphericalImages, areSphericalsVisible]);

  const viewerUnitSystem = useMemo(() => {
    return {
      METRIC: UnitSystemEnum.METRIC,
      IMPERIAL: UnitSystemEnum.IMPERIAL,
    }[unitSystem];
  }, [unitSystem]);

  const showMaxNeighbouringImages = useMemo(() => {
    if (isBetaUser && areNeighbouringImagesVisible) return SHOW_MAX_NEIGHBOURING_IMAGES;
    return 0;
  }, [isBetaUser, areNeighbouringImagesVisible]);

  const allImages = useMemo(() => {
    return imageSets?.flatMap((imageSet) => imageSet.images);
  }, [imageSets]);

  const goToNeighbouringImage = useCallback(
    (image: NeighbouringImageProps) => {
      setNextCurrentSpherical({ imageType: ImageType.Rectilinear, ...image.image });
    },
    [setNextCurrentSpherical]
  );

  const currentRectilinearImage =
    currentSpherical?.imageType === ImageType.Rectilinear ? currentSpherical : undefined;

  const { neighbouringImages, imagesByDirection } =
    useNeighbouringImages({
      image: currentRectilinearImage,
      allImages,
      showMaxNeighbouringImages,
      maxNeighbouringImageAngle: MAX_NEIGHBOURING_IMAGE_ANGLE,

      onClick: goToNeighbouringImage,
      onHover: () => {},
    }) ?? {};

  // TODO: update colors and font size of cube and compass
  const abyssViewerComponentProps: AbyssViewerComponentProps = useMemo(
    () => ({
      selectionBoxIsEnabled: false,
      onInitialPointCloudLoad: centerOnLoad,
      cameraTarget,
      pointCloudMaterials,
      pointClouds,
      edlStrength,
      northRotation: inspectionMetadata?.config?.northRotation || undefined,
      enableEDL: !!edlStrength,
      models,
      visibilityBox: activeVisibilityBox,
      viewCube,
      floorLevels,
      selectionColors,
      showPointCloudsInSphericalView: true,
      imageSets,
      sphericalImageSets,
      sphericalImageVisibilityRange: SPHERICAL_IMAGE_VISIBILITY_RANGE,
      fileLoaderOptions: { withCredentials: true },
      enableLoaderRetry: true,
      polygons,
      markers,
      svgMarkerSets,
      measurementLines,
      onPointCloudSelection,
      onPointCloudClick,
      currentImage: currentSpherical,
      setCurrentImage: setCurrentSpherical,
      nextCurrentImage: nextCurrentSpherical,
      setNextCurrentImage: setNextCurrentSpherical,
      onSphericalViewClick,
      unitSystem: viewerUnitSystem,
      onFileLoadError,
      imageBrightness: brightness,
      imageContrast: contrast,
      htmlMarkerSets,
      smallVisibilityBox,
      setSmallVisibilityBox,
    }),
    [
      centerOnLoad,
      cameraTarget,
      pointCloudMaterials,
      pointClouds,
      edlStrength,
      models,
      inspectionMetadata?.config?.northRotation,
      activeVisibilityBox,
      viewCube,
      floorLevels,
      selectionColors,
      imageSets,
      sphericalImageSets,
      polygons,
      markers,
      svgMarkerSets,
      measurementLines,
      onPointCloudSelection,
      onPointCloudClick,
      currentSpherical,
      setCurrentSpherical,
      nextCurrentSpherical,
      setNextCurrentSpherical,
      onSphericalViewClick,
      viewerUnitSystem,
      onFileLoadError,
      brightness,
      contrast,
      htmlMarkerSets,
      smallVisibilityBox,
      setSmallVisibilityBox,
    ]
  );

  const updateCameraTarget = useCallback(() => {
    if (callbacks?.getCameraPosition) {
      const currentCameraTarget = callbacks?.getCameraPosition();
      if (currentCameraTarget) {
        updateCameraTargetUrlParameters(currentCameraTarget);
      }
    }
  }, [callbacks]);

  const { performanceAlert, onMajorPerformanceCaveat } = useViewerPerformanceAlert();

  const inactiveMarkingFlow = useMemo(() => {
    return (
      editPointOfInterest.state !== 'WaitingForTransition' &&
      editPointOfInterest.state !== 'WaitingForPoint' &&
      editPointOfInterest.state !== 'WaitingForDetail'
    );
  }, [editPointOfInterest.state]);

  const overlayNode = useMemo(() => {
    return (
      <>
        {(!isInitialPointCloudLoaded || assemblyLoading || !structureRelationship.loaded) && (
          <PageLoader background="dark" />
        )}

        {selectedSpherical && inactiveMarkingFlow && (
          <ShadowBar>
            <MenuBar updateCameraTarget={updateCameraTarget} />
          </ShadowBar>
        )}
        {!selectedSpherical && inactiveMarkingFlow && (
          <MenuBar updateCameraTarget={updateCameraTarget} />
        )}

        {loadErrorAlert}
        {performanceAlert}
      </>
    );
  }, [
    assemblyLoading,
    inactiveMarkingFlow,
    isInitialPointCloudLoaded,
    loadErrorAlert,
    performanceAlert,
    selectedSpherical,
    structureRelationship.loaded,
    updateCameraTarget,
  ]);

  return (
    <>
      <AbyssViewerNoContext
        abyssViewerComponentProps={abyssViewerComponentProps}
        displayAnimationEditor={displayAnimationEditor}
        onMajorPerformanceCaveat={onMajorPerformanceCaveat}
        overlayNode={overlayNode}
      >
        <NeighbouringImageSet images={neighbouringImages} />
        <ImagesByDirection
          images={imagesByDirection}
          goToNeighbouringImage={goToNeighbouringImage}
        />
        {children}
      </AbyssViewerNoContext>
      {isBetaUser && <LowFpsPopupTrigger />}
    </>
  );
};
